import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import React, { useMemo } from 'react';
import { FormattedMessage as ReactIntlFormattedMessage, FormattedHTMLMessage as ReactIntlHTMLFormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import useIntl from '../useIntl';
import { detectHTML } from '../utils';
var nbsp = "\xA0";

/**
 * A wrapped version of `FormattedMessage` from `react-intl` that supports the
 * `highlightBundles` option. If `highlightBundles` is disabled, then there is
 * no difference.
 */
export default function FormattedMessage(_ref) {
  var children = _ref.children,
      isHTML = _ref.isHTML,
      whiteSpace = _ref.whiteSpace,
      props = _objectWithoutProperties(_ref, ["children", "isHTML", "whiteSpace"]);

  var intl = useIntl();

  if (!intl) {
    throw new Error('FormattedMessage must be rendered inside an IntlProvider supplied by @techstyle/react-intl.');
  } // TO-DO: Remove these "any" when the intl module gets typed


  var highlightBundles = useSelector(function (state) {
    return state.intl.highlightBundles;
  });
  var resourceBundles = useSelector(function (state) {
    return highlightBundles ? state.intl.resourceBundles : null;
  });
  var _intl$messages$props$ = intl.messages[props.id],
      message = _intl$messages$props$ === void 0 ? props.defaultMessage : _intl$messages$props$;
  isHTML = useMemo(function () {
    if (isHTML != null) {
      return isHTML;
    }

    if (message == null) {
      return false;
    }

    return detectHTML(message);
  }, [isHTML, message]);
  var MessageComponent = isHTML ? ReactIntlHTMLFormattedMessage : ReactIntlFormattedMessage;
  var renderMessage = useMemo(function () {
    // If a custom `children` prop was already supplied, we can't safely
    // highlight it without potentially affecting layout, so bail out. Consumers
    // should use custom `children` as a last resort; try `tagName` first.
    if (children) {
      return children;
    }

    var isHighlight = function isHighlight(v) {
      return v.highlightBundles === true;
    };

    var TextComponent = intl.textComponent;
    var messageProps = {
      as: props.tagName,
      highlightBundles: highlightBundles,
      whiteSpace: whiteSpace
    };

    if (isHighlight(messageProps)) {
      // Determine whether both the group and key are populated.
      var localeBundles = resourceBundles[intl.locale];
      var groupCode = props.id.split('.', 1)[0];
      var isGroupLoaded = localeBundles != null && localeBundles[groupCode] != null;
      var isKeyLoaded = intl.messages[props.id] != null; // Generate a status message to show in the `title` attribute.

      messageProps.title = "".concat(isGroupLoaded && isKeyLoaded ? '✔︎' : '✘').concat(nbsp).concat(props.id);
      messageProps.isGroupLoaded = isGroupLoaded;
      messageProps.isKeyLoaded = isKeyLoaded;
    }

    if (isHTML) {
      // eslint-disable-next-line react/display-name
      return function (html) {
        return /*#__PURE__*/React.createElement(TextComponent, _extends({}, messageProps, {
          dangerouslySetInnerHTML: {
            __html: html
          }
        }));
      };
    } // eslint-disable-next-line react/display-name


    return function () {
      for (var _len = arguments.length, nodes = new Array(_len), _key = 0; _key < _len; _key++) {
        nodes[_key] = arguments[_key];
      }

      return /*#__PURE__*/React.createElement.apply(React, [TextComponent, messageProps].concat(nodes));
    };
  }, [children, highlightBundles, intl.locale, intl.messages, intl.textComponent, isHTML, props.id, props.tagName, resourceBundles, whiteSpace]);
  return /*#__PURE__*/React.createElement(MessageComponent, props, renderMessage);
}